/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : APMSettingsForm.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : APMSettings Form component
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Dec 30, 2021
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC } from "react";
import { Card, Grid, Button, Badge } from "@scuf/common";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { APMSettingsInputGroup } from "./APMSettingsInputGroup";
import { APMSettingsItemSelect } from "./APMSettingsItemSelect";
import {
  APMSettingsViewFields,
  APMSettingField,
  EnumAPMSettingsTypeOfFields,
  EnumAPMSettingsDataTypeOfFields,
} from "models/apmSettings";
import {
  selectCurrentApmFileRecordApmSettings,
  setCurrentWorkFlowStep,
  setCurrentWorkFlowType,
} from "stores/ApmWorkFlowStore";
import { SaveAPMFileModal } from "components/SaveAPMFileModal";
import {
  selectSaveFileModalOpen,
  selectSaveDetailsLoading,
  setUpdatedAPMSettingsDetails,
  selectUpdatedAPMSettingsData,
  SaveAPMSettingsData,
} from "stores/ApmSettingsStore";
import { EnumApmSettingsWorkFlow } from "models/configuration";

export const APMSettingsForm: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedSaveFileModalOpen = useSelector(selectSaveFileModalOpen);
  const saveAPMDetailsLoader = useSelector(selectSaveDetailsLoading);
  const apmSettingsMasterFields = useSelector(
    selectCurrentApmFileRecordApmSettings
  );
  const apmSettingsUpdatedFields = useSelector(selectUpdatedAPMSettingsData);
  const initializeValue = {
    id: 0,
    name: "",
    index: 0,
    type: 0,
    dataType: 0,
    value: "",
    minValue: "",
    maxValue: "",
    floatResolution: 0,
    numberOfOption: 0,
    fieldGrouping: "",
    optionAvailable: {},
  };

  const apmSettingsFields =
    apmSettingsUpdatedFields !== null
      ? apmSettingsUpdatedFields
      : apmSettingsMasterFields;

  const defaultValuesOfForms = {
    apmSettings: apmSettingsFields
      ? apmSettingsFields
      : [{ ...initializeValue }],
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<APMSettingsViewFields>({
    defaultValues: defaultValuesOfForms,
    mode: "all",
  });

  const FieldValues = watch();

  const onClickSaveAndOtherAction = (
    data: APMSettingsViewFields,
    clickedButton: string
  ) => {
    dispatch(setUpdatedAPMSettingsDetails(data?.apmSettings));
    dispatch(SaveAPMSettingsData(data?.apmSettings, clickedButton));
    if (clickedButton === "SaveAndPreview") {
      dispatch(setCurrentWorkFlowType(EnumApmSettingsWorkFlow.Preview));
      navigate("/preview");
    }
  };

  const onChangeHandler = (item: string, value: string, index: number) => {
    setValue(`apmSettings.${index}.value`, value, { shouldValidate: true });
  };

  const registerHandler = (index: number, apmFieldData: APMSettingField) => {
    let minLengthOfField = false;
    let maxLengthOfField = false;
    let minPositiveOfField = false;
    let maxPositiveOfField = false;
    let minNegativeOfField = false;
    let maxNegativeOfField = false;
    let minFloatValueOfField = false;
    let maxFloatValueOfField = false;
    let bytesValueOfField = false;
    let isRequired = true;

    if (
      apmFieldData &&
      apmFieldData.dataType ===
        EnumAPMSettingsDataTypeOfFields.MinimumPositiveNegativeInteger
    ) {
      minNegativeOfField = true;
      maxNegativeOfField = true;
    } else if (
      apmFieldData &&
      apmFieldData.dataType ===
        EnumAPMSettingsDataTypeOfFields.MinimumNumberOfCharacters
    ) {
      minLengthOfField = true;
      maxLengthOfField = true;
      if (parseInt(apmFieldData.minValue) === 0) {
        isRequired = false;
      }
    } else if (
      apmFieldData &&
      apmFieldData.dataType ===
        EnumAPMSettingsDataTypeOfFields.MinimumPositiveInteger
    ) {
      minPositiveOfField = true;
      maxPositiveOfField = true;
    } else if (
      apmFieldData &&
      apmFieldData.dataType ===
        EnumAPMSettingsDataTypeOfFields.MinimumFloatingNumber
    ) {
      minFloatValueOfField = true;
      maxFloatValueOfField = true;
    } else if (
      apmFieldData &&
      apmFieldData.dataType ===
        EnumAPMSettingsDataTypeOfFields.MinimumNumberOfBytes
    ) {
      bytesValueOfField = true;
      if (parseInt(apmFieldData.minValue) === 0) {
        isRequired = false;
      }
    }

    register(`apmSettings.${index}.value` as const, {
      required: {
        value: isRequired,
        message: `Enter a valid ${apmFieldData?.name}`,
      },
      validate: {
        minlength: (value) => {
          if (minLengthOfField) {
            return (
              value.length >= parseInt(apmFieldData?.minValue) ||
              `Expected minimum of ${apmFieldData?.minValue} character`
            );
          }
          return true;
        },
        maxlength: (value) => {
          if (maxLengthOfField) {
            return (
              value.length <= parseInt(apmFieldData?.maxValue) ||
              `Expected maximum of ${apmFieldData?.maxValue} characters`
            );
          }
          return true;
        },

        positiveIntegers: (value) => {
          if (minPositiveOfField || maxPositiveOfField) {
            return (
              /^[0-9]*$/.test(value) ||
              `Enter the number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },

        NegativeIntegers: (value) => {
          if (minNegativeOfField || maxNegativeOfField) {
            return (
              /^-?[0-9]*$/.test(value) ||
              `Enter the number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },

        minPositive: (value) => {
          if (minPositiveOfField) {
            return (
              parseInt(value) >= parseInt(apmFieldData?.minValue) ||
              `Enter the number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },
        maxPositive: (value) => {
          if (maxPositiveOfField) {
            return (
              parseInt(value) <= parseInt(apmFieldData?.maxValue) ||
              `Enter the number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },

        minNegative: (value) => {
          if (minNegativeOfField) {
            return (
              parseInt(value) >= parseInt(apmFieldData?.minValue) ||
              `Enter the number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },
        maxNegative: (value) => {
          if (maxNegativeOfField) {
            return (
              parseInt(value) <= parseInt(apmFieldData?.maxValue) ||
              `Enter the number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },

        floatValueValidate: (value) => {
          if (minFloatValueOfField || maxFloatValueOfField) {
            return (
              /\.\d+$/.test(value) ||
              `Enter the valid decimal number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },
        minFloatValue: (value) => {
          if (minFloatValueOfField) {
            return (
              parseFloat(value) >= parseFloat(apmFieldData?.minValue) ||
              `Enter the decimal number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },
        maxFloatValue: (value) => {
          if (maxFloatValueOfField) {
            return (
              parseFloat(value) <= parseFloat(apmFieldData?.maxValue) ||
              `Enter the decimal number between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue}`
            );
          }
          return true;
        },
        maxBytesValue: (value) => {
          if (bytesValueOfField) {
            const byteMinLength = parseInt(apmFieldData?.minValue) * 2;
            const byteMaxLength = parseInt(apmFieldData?.maxValue) * 2;
            const regexFormat = new RegExp(
              `^(0x|0X)?[a-fA-F0-9]{${byteMinLength},${byteMaxLength}}$`
            );
            return (
              regexFormat.test(value) ||
              `Enter the hexadecimal value between ${apmFieldData?.minValue} to ${apmFieldData?.maxValue} bytes`
            );
          }
          return true;
        },
      },
      pattern: {
        value:
          maxPositiveOfField ||
          minPositiveOfField ||
          minFloatValueOfField ||
          maxFloatValueOfField ||
          minNegativeOfField ||
          maxNegativeOfField
            ? /^\d*[0-9.-]+\d*$/
            : new RegExp(""),
        message: `Enter a valid ${apmFieldData?.name}.It must contain number(0-9)`,
      },
    });
  };

  return (
    <div>
      <form>
        <div className="apm-header">
          <div className="reqiure-field-label">
            Enter the below details (Fields marked with
            <Badge
              color="red"
              empty={true}
              className="asterix-replacement"
            ></Badge>
            are required){" "}
          </div>
          <div className="clear-button">
            <Button
              type="secondary"
              actionType="reset"
              content="Clear All"
              onClick={() => {
                reset(defaultValuesOfForms);
              }}
            />
          </div>
        </div>
        <Card>
          <Card.Content>
            <Grid className="apm-settings-view">
              {FieldValues &&
                FieldValues?.apmSettings.map((apmField, index) => {
                  return (
                    <Grid.Row key={index}>
                      <Grid.Column lWidth={4} xsWidth={12} key={apmField.id}>
                        {apmField.type ===
                          EnumAPMSettingsTypeOfFields.NonEditableTextField && (
                          <APMSettingsInputGroup
                            name={`apmSettings.${index}.value`}
                            label={apmField.name}
                            value={apmField.value}
                            onChange={onChangeHandler}
                            index={index}
                            registerName={registerHandler}
                            disabled={true}
                            apmFieldValue={apmField}
                          />
                        )}

                        {apmField.type ===
                          EnumAPMSettingsTypeOfFields.EditableTextField && (
                          <APMSettingsInputGroup
                            name={`apmSettings.${index}.value`}
                            label={apmField.name}
                            value={apmField.value}
                            onChange={onChangeHandler}
                            index={index}
                            registerName={registerHandler}
                            apmFieldValue={apmField}
                            error={errors && errors.apmSettings?.[index]?.value}
                          />
                        )}
                        {apmField.type ===
                          EnumAPMSettingsTypeOfFields.ListSelection && (
                          <APMSettingsItemSelect
                            name={`apmSettings.${index}.value`}
                            label={apmField.name}
                            value={apmField.value}
                            dataset={apmField.optionAvailable}
                            onChange={onChangeHandler}
                            index={index}
                            registerName={registerHandler}
                            apmFieldValue={apmField}
                          />
                        )}
                      </Grid.Column>
                    </Grid.Row>
                  );
                })}
            </Grid>
          </Card.Content>
        </Card>
        <Grid className="bottom-button-grid">
          <Grid.Row>
            <Grid.Column lWidth={3} xsWidth={12}>
              <Button
                type="secondary"
                size="medium"
                content="Back"
                disabled={saveAPMDetailsLoader}
                onClick={() => {
                  dispatch(setCurrentWorkFlowStep(3));
                }}
              />
            </Grid.Column>
            <Grid.Column lWidth={9} xsWidth={12}>
              <Grid.Row className="apm-settings-right-bottom-buttons">
                <Button
                  actionType="submit"
                  type="secondary"
                  size="medium"
                  content="Save"
                  disabled={saveAPMDetailsLoader}
                  onClick={handleSubmit((data) =>
                    onClickSaveAndOtherAction(data, "Save")
                  )}
                />

                <Button
                  type="secondary"
                  actionType="submit"
                  size="medium"
                  content="Save & Preview"
                  disabled={saveAPMDetailsLoader}
                  onClick={handleSubmit((data) =>
                    onClickSaveAndOtherAction(data, "SaveAndPreview")
                  )}
                />

                <Button
                  type="primary"
                  actionType="submit"
                  size="medium"
                  content="Save & Generate APM File"
                  disabled={saveAPMDetailsLoader}
                  onClick={handleSubmit((data) =>
                    onClickSaveAndOtherAction(data, "SaveAndGenerate")
                  )}
                />
              </Grid.Row>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </form>
      {selectedSaveFileModalOpen && <SaveAPMFileModal />}
    </div>
  );
};
